import { CalendarClock, ChevronRight } from 'lucide-react'
import { Body, Caption } from '@meiuca/ui-components'
import { formatCurrency, getDayOfWeekName, parseAPIDate } from '@meiuca/web-foundation'
import { useComponentImpression } from '@meiuca/analytics'
import { COMPONENT_TYPES } from '@/constants/analyticsEvents'
import type { VenueMembershipItem } from '@/services/venueMembershipService'

interface VenueMembershipCardProps {
  membership: VenueMembershipItem
  onClick?: () => void
  position?: number
}

export function VenueMembershipCard({ membership, onClick, position }: VenueMembershipCardProps) {
  const impressionRef = useComponentImpression({
    componentType: COMPONENT_TYPES.VENUE_MEMBERSHIP_CARD,
    componentKey: `venue_membership_${membership.id}`,
    position,
  })
  const interactive = Boolean(onClick)
  const outstandingCount = membership.outstandingPayments.length
  const ended = !!membership.endDate && parseAPIDate(membership.endDate).getTime() < Date.now()

  return (
    <div
      ref={impressionRef}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick?.()
              }
            }
          : undefined
      }
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      className={`rounded-lg bg-background-white border border-border-light p-4 transition-shadow hover:shadow-md ${
        interactive ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-primary/40' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <Body className="text-base font-semibold text-text-primary mb-1">{membership.venueName}</Body>
          <Caption className="text-text-secondary flex items-center gap-1">
            <CalendarClock className="w-3 h-3 flex-shrink-0" />
            <span>Toda {getDayOfWeekName(membership.weekday, 'long').toLowerCase()}</span>
          </Caption>
        </div>
        {ended ? (
          <span className="px-2 py-1 rounded text-xs font-medium bg-background-secondary text-text-secondary">
            Finalizado
          </span>
        ) : (
          <span className="px-2 py-1 rounded text-xs font-medium bg-feedback-success/20 text-feedback-success">
            Ativo
          </span>
        )}
      </div>

      <div className="flex items-center justify-between pt-3 mt-3 border-t border-border-light">
        <div>
          <Body className="text-lg font-semibold text-brand-primary m-0">
            {formatCurrency(membership.totalAmountCents)}
          </Body>
          <Caption className="text-text-secondary">por mês</Caption>
        </div>
        <div className="flex items-center gap-2">
          {outstandingCount > 0 && (
            <span className="px-2 py-1 rounded text-xs font-medium bg-feedback-warning/20 text-feedback-warning">
              {outstandingCount === 1 ? '1 a pagar' : `${outstandingCount} a pagar`}
            </span>
          )}
          {interactive && <ChevronRight className="w-5 h-5 text-text-tertiary" />}
        </div>
      </div>
    </div>
  )
}
